/**
 * ✅ FASE 0: Servicio de métricas para observabilidad de Socket.IO en frontend
 * 
 * Propósito: SOLO MEDIR, no modificar lógica de negocio
 * - Contar recepciones por tipo de evento
 * - Registrar reconexiones y desconexiones
 * - Detectar picos de recepciones que causen re-renders
 */

class SocketMetricsService {
  constructor() {
    // Contadores por tipo de evento
    this.eventCounters = new Map();
    
    // Log detallado de recepciones por ticket (últimos 500)
    this.receptionLog = [];
    this.MAX_LOG_SIZE = 500;
    
    // Tracking de reconexiones
    this.reconnectionCount = 0;
    this.disconnectionCount = 0;
    this.transportCloseCount = 0;
    this.lastDisconnectReason = null;
    
    // Timestamp de inicio
    this.startTime = Date.now();
    
    // Ventana de tiempo para calcular recepciones/minuto
    this.WINDOW_MS = 60000; // 1 minuto
    
    // ✅ COMENTADO: Spam de métricas deshabilitado
    // console.log("📊 [SocketMetrics-Frontend] Servicio de métricas iniciado");
    
    // ✅ COMENTADO: Reporte automático deshabilitado para evitar spam
    // Reportar métricas cada 5 minutos
    // this.metricsInterval = setInterval(() => this.reportMetrics(), 300000);
  }

  /**
   * Registrar una recepción de Socket.IO
   */
  trackReception(eventType, action, ticketId) {
    const now = Date.now();
    
    // Actualizar contador del evento
    const key = `${eventType}:${action || 'unknown'}`;
    const current = this.eventCounters.get(key) || {
      count: 0,
      lastReception: now,
      perMinute: 0
    };
    
    current.count++;
    current.lastReception = now;
    this.eventCounters.set(key, current);
    
    // Log detallado si hay ticketId
    if (ticketId) {
      this.receptionLog.push({
        ticketId,
        eventType,
        action: action || 'unknown',
        timestamp: now
      });
      
      // Mantener solo los últimos MAX_LOG_SIZE
      if (this.receptionLog.length > this.MAX_LOG_SIZE) {
        this.receptionLog.shift();
      }
    }
  }

  /**
   * Registrar reconexión
   */
  trackReconnection() {
    this.reconnectionCount++;
    // ✅ COMENTADO: Spam de métricas deshabilitado
    // console.log(`🔄 [SocketMetrics-Frontend] Reconexión #${this.reconnectionCount}`);
  }

  /**
   * Registrar desconexión
   */
  trackDisconnection(reason) {
    this.disconnectionCount++;
    this.lastDisconnectReason = reason;
    
    if (reason === 'transport close') {
      this.transportCloseCount++;
    }
    
    // ✅ COMENTADO: Spam de métricas deshabilitado
    // console.log(`🔌 [SocketMetrics-Frontend] Desconexión #${this.disconnectionCount}: ${reason}`);
  }

  /**
   * Obtener recepciones por ticket en una ventana de tiempo
   */
  getReceptionsForTicket(ticketId, windowMs = 60000) {
    const cutoff = Date.now() - windowMs;
    return this.receptionLog.filter(
      log => log.ticketId === ticketId && log.timestamp > cutoff
    );
  }

  /**
   * Obtener recepciones por minuto para un tipo de evento
   */
  getReceptionsPerMinute(eventType, action) {
    const now = Date.now();
    const cutoff = now - this.WINDOW_MS;
    
    // Contar recepciones en la última ventana
    const recentReceptions = this.receptionLog.filter(log => {
      if (log.timestamp < cutoff) return false;
      if (log.eventType !== eventType) return false;
      if (action && log.action !== action) return false;
      return true;
    });
    
    return recentReceptions.length;
  }

  /**
   * Reportar métricas actuales
   */
  reportMetrics() {
    // ✅ COMENTADO: Spam de métricas deshabilitado - Solo disponible vía window.socketMetrics.reportNow()
    // const uptime = (Date.now() - this.startTime) / 1000 / 60; // minutos
    // console.log("📊 [SocketMetrics-Frontend] ========== REPORTE DE MÉTRICAS ==========");
    // console.log(`📊 [SocketMetrics-Frontend] Tiempo activo: ${uptime.toFixed(1)} minutos`);
    // console.log(`📊 [SocketMetrics-Frontend] Total recepciones registradas: ${this.receptionLog.length}`);
    // console.log(`📊 [SocketMetrics-Frontend] Reconexiones: ${this.reconnectionCount}`);
    // console.log(`📊 [SocketMetrics-Frontend] Desconexiones: ${this.disconnectionCount}`);
    // console.log(`📊 [SocketMetrics-Frontend] Transport close: ${this.transportCloseCount}`);
    
    // if (this.lastDisconnectReason) {
    //   console.log(`📊 [SocketMetrics-Frontend] Última razón desconexión: ${this.lastDisconnectReason}`);
    // }
    
    // ✅ COMENTADO: Variables no usadas para evitar warnings
    // const sortedEvents = Array.from(this.eventCounters.entries())
    //   .sort((a, b) => b[1].count - a[1].count);
    
    // if (sortedEvents.length > 0) {
    //   console.log("📊 [SocketMetrics-Frontend] Recepciones por tipo de evento:");
    //   sortedEvents.forEach(([key, metrics]) => {
    //     const perMin = this.getReceptionsPerMinuteForKey(key);
    //     console.log(
    //       `📊 [SocketMetrics-Frontend]   ${key}: ${metrics.count} total | ${perMin}/min`
    //     );
    //   });
    // }
    
    // ✅ COMENTADO: Variables no usadas para evitar warnings
    // const activeTickets = this.getMostActiveTickets(300000, 5);
    // if (activeTickets.length > 0) {
    //   console.log("📊 [SocketMetrics-Frontend] Tickets más activos (últimos 5 min):");
    //   activeTickets.forEach(({ ticketId, count }) => {
    //     console.log(`📊 [SocketMetrics-Frontend]   - Ticket ${ticketId}: ${count} recepciones`);
    //   });
    // }
    
    // console.log("📊 [SocketMetrics-Frontend] ===========================================");
  }

  /**
   * Obtener recepciones/minuto para un key específico
   */
  getReceptionsPerMinuteForKey(key) {
    const [eventType, action] = key.split(":");
    return this.getReceptionsPerMinute(eventType, action);
  }

  /**
   * Obtener tickets más activos
   */
  getMostActiveTickets(windowMs, limit) {
    const cutoff = Date.now() - windowMs;
    const ticketCounts = new Map();
    
    this.receptionLog
      .filter(log => log.timestamp > cutoff)
      .forEach(log => {
        const current = ticketCounts.get(log.ticketId) || 0;
        ticketCounts.set(log.ticketId, current + 1);
      });
    
    return Array.from(ticketCounts.entries())
      .map(([ticketId, count]) => ({ ticketId, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }

  /**
   * Obtener métricas actuales (para debugging)
   */
  getMetrics() {
    const uptime = (Date.now() - this.startTime) / 1000 / 60;
    
    return {
      uptime,
      totalReceptions: this.receptionLog.length,
      reconnectionCount: this.reconnectionCount,
      disconnectionCount: this.disconnectionCount,
      transportCloseCount: this.transportCloseCount,
      lastDisconnectReason: this.lastDisconnectReason,
      eventCounters: Object.fromEntries(this.eventCounters),
      recentReceptions: this.receptionLog.slice(-100) // Últimas 100
    };
  }

  /**
   * Resetear métricas (útil para pruebas o inicio de nueva medición)
   */
  reset() {
    this.eventCounters.clear();
    this.receptionLog = [];
    this.reconnectionCount = 0;
    this.disconnectionCount = 0;
    this.transportCloseCount = 0;
    this.lastDisconnectReason = null;
    this.startTime = Date.now();
    // ✅ COMENTADO: Spam de métricas deshabilitado
    // console.log("📊 [SocketMetrics-Frontend] Métricas reseteadas");
  }

  /**
   * Detectar si un ticket tiene recepciones anormales
   */
  detectAnomalies(ticketId, thresholdPerMinute = 10) {
    const receptions = this.getReceptionsForTicket(ticketId, 60000);
    
    if (receptions.length > thresholdPerMinute) {
      console.warn(
        `⚠️ [SocketMetrics-Frontend] ANOMALÍA: Ticket ${ticketId} tiene ${receptions.length} recepciones en el último minuto`
      );
      return true;
    }
    
    return false;
  }

  /**
   * Cleanup al desmontar
   */
  cleanup() {
    if (this.metricsInterval) {
      clearInterval(this.metricsInterval);
    }
  }
}

// Exportar instancia singleton
const socketMetricsService = new SocketMetricsService();

// Exponer en window para debugging en consola
if (typeof window !== 'undefined') {
  window.socketMetrics = {
    getMetrics: () => socketMetricsService.getMetrics(),
    reportNow: () => socketMetricsService.reportMetrics(),
    reset: () => socketMetricsService.reset(),
    getReceptionsForTicket: (ticketId) => socketMetricsService.getReceptionsForTicket(ticketId)
  };
  // ✅ COMENTADO: Spam de métricas deshabilitado
  // console.log("💡 [SocketMetrics-Frontend] Funciones disponibles en window.socketMetrics:");
  // console.log("  - window.socketMetrics.getMetrics()");
  // console.log("  - window.socketMetrics.reportNow()");
  // console.log("  - window.socketMetrics.reset()");
  // console.log("  - window.socketMetrics.getReceptionsForTicket(ticketId)");
}

export default socketMetricsService;

